<template>
  <div v-if="open" class="search-kb">
    <div class="search-kb__backdrop" @click="$emit('close')"></div>
    <div class="search-kb__panel" @mousedown.prevent @touchstart.stop>
      <div class="search-kb__top">
        <div class="search-kb__langs">
          <button
            type="button"
            class="search-kb__lang"
            :class="{ 'is-active': layout === 'kk' }"
            @click="layout = 'kk'"
          >
            ҚАЗ
          </button>
          <button
            type="button"
            class="search-kb__lang"
            :class="{ 'is-active': layout === 'ru' }"
            @click="layout = 'ru'"
          >
            РУС
          </button>
          <button
            type="button"
            class="search-kb__lang"
            :class="{ 'is-active': layout === 'num' }"
            @click="layout = 'num'"
          >
            123
          </button>
        </div>
        <button type="button" class="search-kb__done" @click="$emit('close')">
          {{ $t('cash.searchKeyboard.done') }}
        </button>
      </div>

      <div class="search-kb__value">
        <span>{{ value || $t('cash.searchKeyboard.empty') }}</span>
        <button
          v-if="value"
          type="button"
          class="search-kb__clear"
          @click="$emit('input', '')"
        >
          <i class="mdi mdi-close-circle"></i>
        </button>
      </div>

      <div class="search-kb__rows">
        <div
          v-for="(row, rowIndex) in currentRows"
          :key="`${layout}-row-${rowIndex}`"
          class="search-kb__row"
          :class="{ 'search-kb__row--bottom': rowIndex === currentRows.length - 1 }"
        >
          <button
            v-for="(key, keyIndex) in row"
            :key="`${layout}-${rowIndex}-${keyIndex}-${key}`"
            type="button"
            class="search-kb__key"
            :class="{
              'search-kb__key--space': key === 'space',
              'search-kb__key--action': key === 'back' || key === 'shift',
              'search-kb__key--shift-on': key === 'shift' && shifted,
            }"
            @click="press(key)"
          >
            <i v-if="key === 'back'" class="mdi mdi-backspace-outline"></i>
            <i
              v-else-if="key === 'shift'"
              class="mdi"
              :class="shifted ? 'mdi-arrow-up-bold' : 'mdi-arrow-up-bold-outline'"
            ></i>
            <template v-else-if="key === 'space'">{{ $t('cash.searchKeyboard.space') }}</template>
            <template v-else>{{ displayKey(key) }}</template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Стандартная раскладка ЙЦУКЕН + казахские буквы сверху
const KK_ROWS = [
  ['ә', 'і', 'ң', 'ғ', 'ү', 'ұ', 'қ', 'ө', 'һ'],
  ['й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х'],
  ['ф', 'ы', 'в', 'а', 'п', 'р', 'о', 'л', 'д', 'ж', 'э'],
  ['shift', 'я', 'ч', 'с', 'м', 'и', 'т', 'ь', 'б', 'ю', 'back'],
  ['space'],
]

const RU_ROWS = [
  ['й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х', 'ъ'],
  ['ф', 'ы', 'в', 'а', 'п', 'р', 'о', 'л', 'д', 'ж', 'э'],
  ['shift', 'я', 'ч', 'с', 'м', 'и', 'т', 'ь', 'б', 'ю', 'back'],
  ['space'],
]

const NUM_ROWS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['-', '0', '.', 'back'],
  ['space'],
]

export default {
  name: 'SearchKeyboard',
  props: {
    open: { type: Boolean, default: false },
    value: { type: String, default: '' },
    locale: { type: String, default: 'kk' },
  },
  data() {
    return {
      layout: this.locale === 'ru' ? 'ru' : 'kk',
      shifted: false,
    }
  },
  computed: {
    currentRows() {
      if (this.layout === 'num') return NUM_ROWS
      return this.layout === 'ru' ? RU_ROWS : KK_ROWS
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.layout = this.locale === 'ru' ? 'ru' : 'kk'
        this.shifted = false
      }
    },
    locale(next) {
      if (this.open && (next === 'kk' || next === 'ru')) {
        this.layout = next
      }
    },
  },
  methods: {
    displayKey(key) {
      if (!this.shifted) return key
      return String(key).toLocaleUpperCase(this.layout === 'ru' ? 'ru-RU' : 'kk-KZ')
    },
    press(key) {
      if (key === 'shift') {
        this.shifted = !this.shifted
        return
      }
      if (key === 'back') {
        this.$emit('input', String(this.value || '').slice(0, -1))
        return
      }
      if (key === 'space') {
        this.$emit('input', `${this.value || ''} `)
        return
      }
      const char = this.displayKey(key)
      this.$emit('input', `${this.value || ''}${char}`)
      if (this.shifted) this.shifted = false
    },
  },
}
</script>

<style lang="scss" scoped>
.search-kb {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.search-kb__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
}

.search-kb__panel {
  position: relative;
  z-index: 1;
  width: min(760px, 100%);
  padding: 12px 10px 14px;
  border-radius: 20px 20px 0 0;
  background: #eef2f7;
  border: 1px solid #d7e2ee;
  box-shadow: 0 -12px 40px rgba(18, 32, 51, 0.18);
}

.search-kb__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.search-kb__langs {
  display: flex;
  gap: 6px;
}

.search-kb__lang,
.search-kb__done {
  min-height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #d7e2ee;
  background: #fff;
  font-weight: 750;
  cursor: pointer;
}

.search-kb__lang.is-active {
  background: #0b4f8a;
  border-color: #0b4f8a;
  color: #fff;
}

.search-kb__done {
  color: #0b4f8a;
}

.search-kb__value {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  margin-bottom: 10px;
  padding: 0 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #d7e2ee;
  font-size: 18px;
  font-weight: 700;
  color: #122033;

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.search-kb__clear {
  border: 0;
  background: transparent;
  color: #8aa0b5;
  cursor: pointer;
  font-size: 20px;
}

.search-kb__rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.search-kb__row {
  display: flex;
  justify-content: center;
  gap: 5px;

  &--bottom {
    margin-top: 2px;
  }
}

.search-kb__key {
  flex: 1 1 0;
  max-width: 58px;
  min-width: 0;
  height: 46px;
  border-radius: 10px;
  border: 1px solid #d7e2ee;
  background: #fff;
  color: #122033;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: #dceaf5;
    border-color: #0b4f8a;
  }

  &--action {
    flex: 1.35 1 0;
    max-width: 72px;
    color: #0b4f8a;
    background: #f7fafc;
  }

  &--shift-on {
    background: #0b4f8a;
    border-color: #0b4f8a;
    color: #fff;
  }

  &--space {
    flex: 1 1 auto;
    max-width: none;
    width: min(420px, 70%);
  }
}

@media (max-width: 640px) {
  .search-kb__key {
    height: 42px;
    font-size: 15px;
    border-radius: 8px;
  }

  .search-kb__row {
    gap: 4px;
  }
}
</style>
